import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

const STATUS_BADGE = {
  active:   { background: '#dcfce7', color: '#16a34a' },
  inactive: { background: '#f3f4f6', color: '#9ca3af' },
};

const EMPTY_FORM = { name: '', description: '', sort_order: 0 };

function CategoryModal({ initial, onClose, onSaved }) {
  const [form,   setForm]   = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Category name is required');
    try {
      setSaving(true);
      if (initial?.id) {
        await API.put(`/expenses/categories/${initial.id}`, form);
        toast.success('Category updated');
      } else {
        await API.post('/expenses/categories', form);
        toast.success('Category created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px', border: '1px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none',
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            {initial?.id ? 'Edit Category' : 'New Category'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af' }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Electricity" />
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              value={form.description || ''}
              onChange={e => set('description', e.target.value)}
              placeholder="Optional description"
            />
          </div>

          <div>
            <label style={labelStyle}>Sort Order</label>
            <input
              type="number" min="0"
              style={inputStyle}
              value={form.sort_order}
              onChange={e => set('sort_order', Number(e.target.value))}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{ padding: '9px 22px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminExpenseCategories() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null); // null | {} | { id, ... }

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/expenses/categories');
      setCategories(data.categories || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleToggle = async (cat) => {
    try {
      await API.patch(`/expenses/categories/${cat.id}/toggle`);
      toast.success(`Category ${cat.status === 'active' ? 'deactivated' : 'activated'}`);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/expenses/categories/${cat.id}`);
      toast.success('Category deleted');
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const thStyle = { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '13px 14px', fontSize: 14, borderBottom: '1px solid #f9fafb', verticalAlign: 'middle' };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>Expense Categories</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Manage categories used to classify business expenses.
          </p>
        </div>
        <button
          onClick={() => setModal(EMPTY_FORM)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
        >
          <FiPlus size={15} /> Add Category
        </button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : categories.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
            No categories yet. Create your first one.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Description</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Sort</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <tr key={cat.id} style={{ opacity: cat.status === 'inactive' ? 0.55 : 1 }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{cat.name}</td>
                  <td style={{ ...tdStyle, color: '#6b7280', maxWidth: 260 }}>{cat.description || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>{cat.sort_order}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 600, ...STATUS_BADGE[cat.status] }}>
                      {cat.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button
                        title={cat.status === 'active' ? 'Deactivate' : 'Activate'}
                        onClick={() => handleToggle(cat)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: cat.status === 'active' ? '#059669' : '#9ca3af', padding: 4 }}
                      >
                        {cat.status === 'active' ? <FiToggleRight size={18} /> : <FiToggleLeft size={18} />}
                      </button>
                      <button
                        title="Edit"
                        onClick={() => setModal(cat)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}
                      >
                        <FiEdit2 size={15} />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => handleDelete(cat)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}
                      >
                        <FiTrash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <CategoryModal
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={fetch}
        />
      )}
    </div>
  );
}